/**
 * The stock Dividend tab's boxes, one payload block each, laid out by `StockDividendTab` on the
 * twelve-column grid. Each box renders nothing when its block has nothing to show, and the tab wraps
 * each one in `Safe`, so a box that fails stays out of the page instead of taking it down.
 */
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { ErrorBoundary } from "react-error-boundary";
import { useAtomValue } from "jotai";
import { useIsomorphicLayoutEffect } from "react-use";
import { Dialog, Tooltip } from "@mui/material";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import TimeSeriesChart, { type RangeButton, type TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";
import { MemoizedDataGrid } from "@/components/data-grid/DataGrid";
import { useDemoMode, useIrThemeVars } from "@/components/index-rebalance-v2/hooks";
import { DIV_ONE_PAGE_ROWS, divStockHistoryGridFetcher } from "@/controllers/dividends-v2";
import IrLogo from "@/components/index-rebalance-v2/IrLogo";
import { Blurred } from "@/components/index-rebalance-v2/ui";
import { alpha } from "@/lib/ortex-charts/color";
import { neutralState, ortexColorState } from "@/lib/store";
import { buildCommentary, cutChance, dateDelta, fmtAmount, fmtBand, fmtBandShort, fmtDate, fmtDateShort, fmtMoney, fmtMonth, fmtPct, fmtPctSigned, fmtProb, frequencyLabel, isNum, outcomeLabel } from "./format";
import {
    DivBorrow,
    DivCoverage,
    DivEventRow,
    DivExBehavior,
    DivHistory,
    DivHistoryGridRow,
    DivLadderRow,
    DivListMembership,
    DivOptionsRow,
    DivOutcome,
    DivOutlook,
    DivPayment,
    DivPeer,
    DivPolicy,
    DivStockResponse,
    DivYield,
} from "@/models/dividends-v2";
import { EvolutionSpark } from "./Evolution";
import { exDateLine, ORIGIN_HELP, ORIGIN_LABEL, scorecardOrigins, trackRecordLine } from "./evolutionData";
import HelpTip from "../HelpTip";
import { demoHistoryGridFetcher } from "./historyFixtures";
import divStyles from "../Dividends.module.scss";
import styles from "./StockDividendTab.module.scss";

/** A box that fails stays out of the page instead of taking it down. */
export const Safe: FC<{ children?: ReactNode }> = ({ children }) => <ErrorBoundary fallback={null}>{children}</ErrorBoundary>;

type BoxProps = { title: string; note?: ReactNode; action?: ReactNode; children: ReactNode; label?: string; help?: ReactNode; tour?: string; maximizable?: boolean };

const BoxTitle: FC<Pick<BoxProps, "title" | "note" | "action" | "help"> & { extra?: ReactNode }> = ({ title, note, action, help, extra }) => (
    <h2 className={styles.title}>
        {title}
        {help && <HelpTip text={help} title={title} />}
        {note && <span className={styles.titleNote}>{note}</span>}
        {action}
        {extra}
    </h2>
);

/**
 * A box: title row (label, the (i) help, optional note, optional link or action on the right) and
 * its body. `tour` is the `data-tour` anchor the page's guided tour points at. `maximizable` adds the
 * app's full-screen icon at the right end of the title row (see `MaximizableBox`).
 */
export const Box: FC<BoxProps> = ({ title, note, action, children, label, help, tour, maximizable }) =>
    maximizable ? (
        <MaximizableBox title={title} note={note} action={action} label={label} help={help} tour={tour}>
            {children}
        </MaximizableBox>
    ) : (
        <section className={styles.box} aria-label={label ?? title} data-tour={tour}>
            <BoxTitle title={title} note={note} action={action} help={help} />
            {children}
        </section>
    );

/**
 * A box that opens full screen (the icon and wording of the module header's full-screen button), in a
 * full-screen dialog under the tab's theme; the icon again or Esc brings it back. The box's content is
 * rendered once, into a node that moves between the box and the dialog, so everything the reader set
 * (Chart or List, Annual or Quarterly, the chart's span) is the same in both and the page's tour
 * anchor stays on the box.
 */
const MaximizableBox: FC<Omit<BoxProps, "maximizable">> = ({ title, note, action, children, label, help, tour }) => {
    const [max, setMax] = useState(false);
    const [host] = useState<HTMLDivElement | null>(() => {
        if (typeof document === "undefined") return null;
        const el = document.createElement("div");
        el.className = styles.boxBody;
        return el;
    });
    const slotRef = useRef<HTMLElement>(null);
    const [dialogSlot, setDialogSlot] = useState<HTMLDivElement | null>(null);
    const themeVars = useIrThemeVars();
    useIsomorphicLayoutEffect(() => {
        const target = max ? dialogSlot : slotRef.current;
        if (!host || !target || host.parentNode === target) return;
        target.appendChild(host);
        // The chart and the grid size themselves to their new room.
        window.dispatchEvent(new Event("resize"));
    }, [host, max, dialogSlot]);
    useEffect(() => () => host?.remove(), [host]);
    const button = (
        <Tooltip title={max ? "Minimize (Esc)" : "Maximize"}>
            <button type="button" className={`${styles.maxButton}${action ? "" : ` ${styles.maxButtonAlone}`}`} aria-label={max ? "Minimize" : "Maximize"} onClick={() => setMax(!max)}>
                {max ? <CloseFullscreenOutlinedIcon fontSize="inherit" /> : <FullscreenOutlinedIcon fontSize="inherit" />}
            </button>
        </Tooltip>
    );
    const content = (
        <>
            <BoxTitle title={title} note={note} action={action} help={help} extra={button} />
            {children}
        </>
    );
    return (
        <section ref={slotRef} className={styles.box} aria-label={label ?? title} data-tour={tour}>
            {host ? createPortal(content, host) : content}
            <Dialog
                fullScreen
                open={max}
                onClose={() => setMax(false)}
                // Above the app bar, below the forecast-path drawer a ladder row opens.
                sx={{ zIndex: (t) => t.zIndex.drawer - 1 }}
                slotProps={{
                    paper: { className: styles.maxPaper, style: themeVars, "aria-label": label ?? title } as object,
                }}
            >
                <div ref={setDialogSlot} className={`${styles.box} ${styles.maxBox}`} />
            </Dialog>
        </section>
    );
};

const TitleLink: FC<{ href: string | null; children: ReactNode }> = ({ href, children }) =>
    href ? (
        <Link href={href} className={styles.titleLink}>
            {children}
        </Link>
    ) : null;

const Fig: FC<{ label: string; value: ReactNode; note?: ReactNode }> = ({ label, value, note }) =>
    value === null || value === undefined ? null : (
        <div className={styles.fig}>
            <span>{label}</span>
            <b>{value}</b>
            {note && <span>{note}</span>}
        </div>
    );

/** A value, or the locked placeholder when the server held it back. Nothing when simply missing. */
const val = (text: string | null, locked: boolean, placeholder = "0.00"): ReactNode => (text !== null ? text : locked ? <Blurred>{placeholder}</Blurred> : null);

const OUTCOME_PILL: Record<DivOutcome, string> = { raise: styles.pillRaise, hold: styles.pillHold, cut: styles.pillCut, omit: styles.pillCut, special: styles.pillSpecial };

export const OutcomePill: FC<{ outcome: DivOutcome | null; probability?: number | null; locked?: boolean }> = ({ outcome, probability, locked }) => {
    if (!outcome) return null;
    const p = fmtProb(probability);
    return (
        <span className={OUTCOME_PILL[outcome] ?? styles.pill}>
            {outcomeLabel(outcome)}
            {p ? (
                ` ${p}`
            ) : locked ? (
                <>
                    {" "}
                    <Blurred>00%</Blurred>
                </>
            ) : null}
        </span>
    );
};

// ─── Tiles ──────────────────────────────────────────────────────────────────

export const hasNext = (p: DivStockResponse): boolean => !!p.next;

/** Next dividend: the amount (declared or forecast), its dates with their bands, and the timing pattern. */
export const NextDividendBox: FC<{ payload: DivStockResponse }> = ({ payload }) => {
    const n = payload.next;
    if (!n) return null;
    const locked = payload.locked || n.locked;
    const declared = n.status === "declared";
    const ccy = n.currency ?? payload.currency;
    const amount = fmtMoney(declared ? (n.declared_amount ?? n.amount_p50) : n.amount_p50, ccy);
    const meta = ["per share", frequencyLabel(n.frequency)].filter(Boolean).join(" · ");
    const row = (label: string, iso: string | null, band: number | null) =>
        iso ? (
            <>
                <dt>{label}</dt>
                <dd>
                    {fmtDate(iso)} {fmtBand(band) && <span className={styles.band}>{fmtBand(band)}</span>}
                </dd>
            </>
        ) : null;
    return (
        <Box
            title="Next dividend"
            tour="div-next"
            help={
                <>
                    The next regular dividend <b>per share</b>, in the currency shown. It is an amount, not a yield (yield has its own box). Each date shows how many business days either side it could fall. Once the company declares, the declared
                    amount and dates replace the forecast.
                </>
            }
            action={n.status === "late" ? <span className={styles.pillLate}>Late{isNum(n.days_late) && n.days_late > 0 ? ` ${n.days_late}d` : ""}</span> : declared ? <span className={styles.pillDeclared}>Declared</span> : null}
        >
            <div className={styles.hero}>
                {val(amount, locked)}
                {meta && <small>{meta}</small>}
            </div>
            {n.period_label && <span className={styles.band}>{n.period_label}</span>}
            <dl className={styles.kv}>
                {row("Declaration", n.declaration_date, n.declaration_band_days)}
                {row("Ex-date", n.ex_date, n.ex_band_days)}
                {row("Record", n.record_date, null)}
                {row("Pay", n.pay_date, null)}
            </dl>
            {n.status === "late" && <div className={styles.status}>The declaration has not come on the usual date{isNum(n.days_late) && n.days_late > 0 ? ` (${n.days_late} days late)` : ""}.</div>}
            {payload.pattern && <div className={styles.foot}>Pattern: {payload.pattern}</div>}
        </Box>
    );
};

type Shares = { raise: number | null; hold: number | null; cut: number | null; special: number | null };

const sharesOf = (o: DivOutlook | null, n: DivEventRow | null): Shares => {
    const src = o ?? n;
    if (!src) return { raise: null, hold: null, cut: null, special: null };
    return { raise: src.p_raise, hold: src.p_hold, cut: cutChance(src), special: src.p_special };
};

export const hasChance = (p: DivStockResponse): boolean => !!p.outlook || !!p.next;

const CHANCE_HELP = (
    <>The chance the next dividend is raised, held, cut or skipped, and the chance of a special dividend. The note under the bar says where the chances come from: the ORTEX forecast or the stock&apos;s own record of raises, holds and cuts.</>
);

/** Chance of a change on the next dividend: the leading outcome, the stacked bar and its legend. */
export const ChanceBox: FC<{ payload: DivStockResponse }> = ({ payload }) => {
    const n = payload.next;
    const o = payload.outlook;
    if (!o && !n) return null;
    const locked = payload.locked || !!n?.locked;
    const s = sharesOf(o, n);
    const known = [s.raise, s.hold, s.cut].some(isNum);
    // The leading outcome is visible to every caller; its probability is not.
    const outcome = n?.outcome ?? (known ? (["raise", "hold", "cut"] as const).reduce((a, b) => ((s[b] ?? 0) > (s[a] ?? 0) ? b : a), "hold" as "raise" | "hold" | "cut") : null);
    const leadP = n?.outcome_probability ?? (outcome && outcome !== "special" && outcome !== "omit" ? s[outcome] : null);
    const segments: { key: string; label: string; value: number | null; cls: string }[] = [
        { key: "raise", label: "raise", value: s.raise, cls: styles.stackRaise },
        { key: "hold", label: "hold", value: s.hold, cls: styles.stackHold },
        { key: "cut", label: "cut", value: s.cut, cls: styles.stackCut },
        { key: "special", label: "special", value: s.special, cls: styles.stackSpecial },
    ];
    const nr = o?.next_raise;
    const all = n?.drivers ?? [];
    // A forecast whose chances are the stock's own record (no model live yet) carries one note driver.
    const historyNote = all.find((d) => d.basis === "history") ?? null;
    const drivers = all.filter((d) => d.basis !== "history").slice(0, 3);
    const ccy = n?.currency ?? payload.currency;
    if (!known && !locked)
        return (
            <Box title="Chance of a change" tour="div-chance" help={CHANCE_HELP}>
                <div className={styles.commentary}>
                    <p>Not enough dividend history yet to give the chance of a raise or a cut.</p>
                </div>
            </Box>
        );
    return (
        <Box title="Chance of a change" tour="div-chance" help={CHANCE_HELP}>
            <div className={styles.hero}>
                {outcomeLabel(outcome) ?? val(null, locked, "Hold")}
                <small>{val(fmtProb(leadP), locked, "00%")}</small>
            </div>
            {known ? (
                <div className={styles.stack} role="img" aria-label={segments.map((g) => `${g.label} ${fmtProb(g.value) ?? "unknown"}`).join(", ")}>
                    {segments.map((g) => (isNum(g.value) && g.value > 0 ? <i key={g.key} className={g.cls} style={{ width: `${Math.min(100, g.value * 100)}%` }} /> : null))}
                </div>
            ) : locked ? (
                <Blurred>
                    <span className={styles.stack} style={{ display: "flex", width: 160 }} />
                </Blurred>
            ) : null}
            <ul className={styles.legend}>
                {segments.map((g) => (
                    <li key={g.key}>
                        <i className={g.cls} />
                        <b>{val(fmtProb(g.value), locked, "00%")}</b> {g.label}
                    </li>
                ))}
            </ul>
            {historyNote && <div className={styles.foot}>{historyNote.label}.</div>}
            {(nr || drivers.length > 0) && (
                <div className={styles.foot}>
                    {nr && (
                        <div>
                            Next raise expected {fmtMonth(nr.ex_date) ?? nr.event_key}
                            {fmtMoney(nr.amount, ccy) ? `: ${fmtMoney(nr.amount, ccy)}` : ""}
                            {fmtProb(nr.probability) ? ` (${fmtProb(nr.probability)})` : ""}
                        </div>
                    )}
                    {drivers.map((d) => (
                        <div key={d.label}>
                            {d.direction && <span className={d.direction === "up" ? styles.up : styles.down}>{d.direction === "up" ? "▲ " : "▼ "}</span>}
                            {d.label}
                        </div>
                    ))}
                </div>
            )}
        </Box>
    );
};

const YIELD_HELP = (
    <>
        Dividends per share over the last close, in per cent. <b>Forward</b> counts the next twelve months of dividends (declared, else forecast); <b>trailing</b> the last twelve months paid. The streak counts years in a row with a raise.
    </>
);

const sectorWord = (y: DivYield): string | null => {
    if (!isNum(y.forward_12m_pct) || !isNum(y.sector_median_pct) || y.sector_median_pct <= 0) return null;
    const r = y.forward_12m_pct / y.sector_median_pct;
    return r < 0.8 ? "Low" : r > 1.25 ? "High" : "In line";
};

/** Yield: forward 12 months large, the next and trailing 12 months, against the sector, and the raise streak. */
export const YieldBox: FC<{ payload: DivStockResponse; forecasts?: boolean }> = ({ payload, forecasts = true }) => {
    const y = payload.yield;
    if (!y) return null;
    const locked = payload.locked;
    const ccy = payload.currency ?? "";
    const amt = (v: number | null) => fmtMoney(v, ccy);
    const payments = isNum(y.payments_per_year) ? `${y.payments_per_year} ${y.payments_per_year === 1 ? "payment" : "payments"}` : null;
    // Before the forecasts are published there is no forward figure: the trailing yield leads.
    if (!forecasts)
        return (
            <Box title="Yield" help={YIELD_HELP}>
                <div className={styles.hero}>
                    {val(fmtPct(y.trailing_12m_pct), locked, "0.00%")}
                    <small>trailing 12m</small>
                </div>
                <div className={styles.figs}>
                    <Fig label="Paid, last 12 months" value={val(amt(y.trailing_12m_amount), locked)} note={payments} />
                    <Fig label="Streak" value={isNum(y.streak_years) ? `${y.streak_years} ${y.streak_years === 1 ? "yr" : "yrs"}` : null} note={isNum(y.streak_years) && y.streak_years > 0 ? "of raises" : null} />
                </div>
            </Box>
        );
    return (
        <Box title="Yield" help={YIELD_HELP}>
            <div className={styles.hero}>
                {val(fmtPct(y.forward_12m_pct), locked, "0.00%")}
                <small>forward 12m</small>
            </div>
            <div className={styles.figs}>
                <Fig label="Next 12 months" value={val(amt(y.forward_12m_amount), locked)} note={payments} />
                <Fig label="Trailing 12 months" value={amt(y.trailing_12m_amount)} note={fmtPct(y.trailing_12m_pct)} />
                <Fig label={y.sector ? `vs ${y.sector}` : "vs sector"} value={sectorWord(y)} note={isNum(y.sector_median_pct) ? `sector median ${fmtPct(y.sector_median_pct)}` : null} />
                <Fig label="Streak" value={isNum(y.streak_years) ? `${y.streak_years} ${y.streak_years === 1 ? "yr" : "yrs"}` : null} note={isNum(y.streak_years) && y.streak_years > 0 ? "of raises" : null} />
            </div>
        </Box>
    );
};

/** "ORTEX track record on AAPL: 23 of 24 amounts exact (since 2020, replayed)", with the ex-dates and each origin (TED #2456). */
export const TrackRecordBox: FC<{ payload: DivStockResponse }> = ({ payload }) => {
    const s = payload.scorecard;
    if (!s) return null;
    const n = s.n_amount ?? s.n;
    if (!n) return null;
    const origins = scorecardOrigins(s);
    const name = payload.ticker || payload.company_name || "this stock";
    return (
        <Box
            title="Track record"
            label={`ORTEX track record on ${name}`}
            tour="div-track-record"
            help={
                <>
                    How often the ORTEX forecast for {name} got the declared amount exactly right, scored {s.note ?? "30 days before each declaration"}, and how often it had the ex-date exact. <b>Replayed</b> forecasts were rebuilt day by day from
                    only what was known at the time; <b>published</b> ones were frozen the day ORTEX published them. Open a past row of the ladder to see how a forecast moved.
                </>
            }
            note={s.note ?? "30 days before declaration"}
        >
            <div className={styles.hero}>
                {s.amount_exact ?? 0} of {n}
                <small>amounts exact</small>
            </div>
            <div className={styles.evoTrackLines}>
                {origins.length > 1 ? (
                    origins.map(({ origin, counts }) => (
                        <div key={origin} title={ORIGIN_HELP[origin]}>
                            <span className={styles.evoOrigin}>{ORIGIN_LABEL[origin]}</span> {trackRecordLine(counts, null)}
                        </div>
                    ))
                ) : (
                    <div title={origins[0] ? ORIGIN_HELP[origins[0].origin] : undefined}>{trackRecordLine(s, s.origin ?? origins[0]?.origin ?? null)}</div>
                )}
                {exDateLine(s) && <div className={styles.dim}>{exDateLine(s)}</div>}
            </div>
        </Box>
    );
};

/**
 * Before this listing's forecasts are published (TED #2467): where the chance tile would be, one
 * neutral line that the forecasts are coming. No product mechanics in the words, by rule.
 */
export const ComingSoonBox: FC = () => (
    <Box title="ORTEX forecast">
        <div className={styles.commentary} data-testid="dividends-coming-soon">
            <p>ORTEX forecasts for this market are coming soon.</p>
        </div>
        <div className={styles.foot}>Until then this tab shows the declared dividends and their history.</div>
    </Box>
);

// ─── Commentary ──────────────────────────────────────────────────────────────

export const hasCommentary = (p: DivStockResponse): boolean => !p.locked && buildCommentary(p).length > 0;

/** Three short paragraphs built by code from this payload's figures. */
export const CommentaryBox: FC<{ payload: DivStockResponse }> = ({ payload }) => {
    if (payload.locked) return null;
    const paras = buildCommentary(payload);
    if (!paras.length) return null;
    return (
        <Box
            title="Commentary"
            help="Short paragraphs written by ORTEX software from the figures on this page: when the next dividend is due, how well it is covered, and what short interest and options say."
            note={payload.as_of ? `from the data on ${fmtDate(payload.as_of, true)}` : null}
        >
            <div className={styles.commentary}>
                {paras.map((p) => (
                    <p key={p.title}>
                        <b>{p.title}.</b> {p.text}
                    </p>
                ))}
            </div>
            <div className={styles.foot}>Generated by ORTEX software from the data on this page, not by a person. Not investment advice.</div>
        </Box>
    );
};

// ─── Forecast ladder ───────────────────────────────────────────────────────────

const resultCell = (r: DivLadderRow): ReactNode => {
    if (!r.past) {
        if (r.status === "late") return <span className={styles.pillLate}>Late</span>;
        if (r.status === "declared") return <span className={styles.pillDeclared}>Declared</span>;
        return <span className={styles.pill}>Forecast</span>;
    }
    const delta = dateDelta(r.forecast_ex_date, r.ex_date);
    const vs = r.declared_vs_forecast;
    if (!vs && !delta) return null;
    return (
        <>
            {vs === "exact" && <span className={styles.ok}>✓ exact</span>}
            {vs === "above" && <span className={styles.miss}>✗ above</span>}
            {vs === "below" && <span className={styles.miss}>✗ below</span>}
            {delta && <span className={styles.band}> {delta}</span>}
        </>
    );
};

/**
 * The next events and the last declared ones, each past forecast kept beside what was declared and
 * (TED #2456) a small chart of how it moved there. A row opens its whole forecast path (`onOpen`).
 */
export const LadderBox: FC<{ payload: DivStockResponse; forecasts?: boolean; onOpen?: (row: DivLadderRow) => void }> = ({ payload, forecasts = true, onOpen }) => {
    const rows = payload.ladder ?? [];
    if (!rows.length) return null;
    if (!forecasts) return <DeclaredLadder payload={payload} />;
    const future = rows.filter((r) => !r.past && !r.horizon).slice(0, 8);
    const later = rows.filter((r) => !r.past && r.horizon);
    const past = rows.filter((r) => r.past).slice(0, 4);
    const locked = payload.locked;
    const money = (v: number | null | undefined, r: DivLadderRow) => fmtMoney(v, r.currency ?? payload.currency);
    // Past rows with a spark (published, Advanced, snapshots) show it in the range column.
    const paths = past.some((r) => r.evolution);
    const laterYear = later.length ? (later[later.length - 1].ex_date ?? "").slice(0, 4) : "";
    const line = (r: DivLadderRow, i: number) => {
        const rowLocked = locked || r.locked;
        // No range while the low and high ends are one amount: it would only repeat the forecast.
        const spread = isNum(r.amount_p10) && isNum(r.amount_p90) && r.amount_p90 - r.amount_p10 > 1e-9;
        const range = spread ? `${money(r.amount_p10, r)}–${money(r.amount_p90, r)}` : isNum(r.amount_p50) ? "—" : null;
        const pastClass = r.past ? `${styles.pastRow}${i === 0 ? ` ${styles.pastStart}` : ""}` : "";
        // Later-year rows have no forecast path of their own to open.
        const open = onOpen && !r.horizon ? () => onOpen(r) : undefined;
        return (
            <tr
                key={r.id}
                className={`${pastClass}${open ? ` ${styles.evoRow}` : ""}` || undefined}
                onClick={open}
                onKeyDown={open ? (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), open()) : undefined}
                tabIndex={open ? 0 : undefined}
                aria-label={open ? `${r.period_label ?? r.event_key}: show how the forecast moved` : undefined}
            >
                <td>{r.period_label}</td>
                <td>
                    {fmtDateShort(r.declaration_date)} {!r.past && fmtBandShort(r.declaration_band_days) && <span className={styles.band}>{fmtBandShort(r.declaration_band_days)}</span>}
                </td>
                <td>{fmtDateShort(r.ex_date)}</td>
                <td>{fmtDateShort(r.pay_date)}</td>
                <td className={styles.num}>{r.past ? val(money(r.amount_p50, r), rowLocked) : <b>{val(money(r.amount_p50, r), rowLocked)}</b>}</td>
                {/* A past row with a path shows how the forecast moved instead of its frozen range (the drawer has both). */}
                <td className={`${styles.num} ${styles.band}`} title={r.past && r.evolution && range ? `Range ${range}` : undefined}>
                    {r.past && r.evolution ? <EvolutionSpark spark={r.evolution} label={r.period_label ?? r.event_key} currency={r.currency ?? payload.currency} /> : val(range, rowLocked, "0.00–0.00")}
                </td>
                <td>{r.horizon ? <span className={styles.band}>–</span> : <OutcomePill outcome={r.outcome} probability={r.outcome_probability} locked={rowLocked} />}</td>
                <td className={styles.num}>{r.past ? val(money(r.declared_amount, r), rowLocked) : null}</td>
                <td>{resultCell(r)}</td>
            </tr>
        );
    };
    return (
        <Box
            title="Forecast ladder"
            tour="div-ladder"
            maximizable
            help={
                <>
                    The next dividends: dates, the forecast amount <b>per share</b> in the dividend&apos;s currency, its likely range (10th to 90th percentile), and the most likely outcome with its chance. Past rows keep the forecast made 30 days
                    before each declaration beside what was declared; where a past row shows a small path, it traces how the forecast moved to the declared amount, and clicking a row opens its whole path. Later years carry the date and the amount
                    range only, and the range widens with each year ahead.
                </>
            }
            note={`next ${future.length} ${future.length === 1 ? "dividend" : "dividends"}${later.length ? ` · then to ${laterYear}` : ""} · amounts per share${past.length ? " · past forecasts kept beside what was declared" : ""}`}
        >
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Period</th>
                            <th>Declaration</th>
                            <th>Ex-date</th>
                            <th>Pay</th>
                            <th className={styles.num}>Forecast / share</th>
                            <th className={styles.num} title={paths ? "Range of the forecast; on past rows, how it moved to the declared amount" : undefined}>
                                {paths ? "Range · path" : "Range"}
                            </th>
                            <th>Chance</th>
                            <th className={styles.num}>Declared</th>
                            <th>Result</th>
                        </tr>
                    </thead>
                    <tbody>
                        {future.map((r) => line(r, -1))}
                        {later.length > 0 && (
                            <tr className={styles.groupRow}>
                                <td colSpan={9}>Later years · the range widens with each year ahead</td>
                            </tr>
                        )}
                        {later.map((r) => line(r, -1))}
                        {past.map((r, i) => line(r, i))}
                    </tbody>
                </table>
            </div>
            <div className={styles.foot}>
                {past.length > 0 && "Forecasts are frozen each day; past rows show the forecast 30 days before the declaration. "}
                {onOpen && "Click a row to see how its forecast moved."}
            </div>
        </Box>
    );
};

/**
 * The ladder before the forecasts are published (TED #2467): the declared dividends only (the
 * upcoming declared ones, then the last four), with their dates and amounts; no forecast, range,
 * chance or result columns, which would all be empty.
 */
const DeclaredLadder: FC<{ payload: DivStockResponse }> = ({ payload }) => {
    const rows = (payload.ladder ?? []).filter((r) => r.past || r.status === "declared");
    if (!rows.length) return null;
    const upcoming = rows.filter((r) => !r.past);
    const past = rows.filter((r) => r.past).slice(0, 4);
    const locked = payload.locked;
    return (
        <Box
            title="Declared dividends"
            tour="div-ladder"
            help="The declared dividends per share, in the dividend's currency: the upcoming ones, then the last four paid."
            note={upcoming.length ? `${upcoming.length} upcoming · the last ${past.length} paid` : `the last ${past.length} declared`}
        >
            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Period</th>
                            <th>Declared</th>
                            <th>Ex-date</th>
                            <th>Record</th>
                            <th>Pay</th>
                            <th className={styles.num}>Amount / share</th>
                        </tr>
                    </thead>
                    <tbody>
                        {[...upcoming, ...past].map((r, i) => (
                            <tr key={r.id} className={r.past ? `${styles.pastRow}${i === upcoming.length ? ` ${styles.pastStart}` : ""}` : undefined}>
                                <td>{r.period_label}</td>
                                <td>{fmtDateShort(r.declaration_date)}</td>
                                <td>{fmtDateShort(r.ex_date)}</td>
                                <td>{fmtDateShort(r.record_date)}</td>
                                <td>{fmtDateShort(r.pay_date)}</td>
                                <td className={styles.num}>{val(fmtMoney(r.declared_amount, r.currency ?? payload.currency), locked || r.locked)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Box>
    );
};

// ─── Dividend history ──────────────────────────────────────────────────────────

/** Per-payment points: the payload's own when TED sends them, else the ladder's declared and forecast rows. */
type PaymentPoint = { ex_date: string; amount: number; forecast: boolean; special: boolean; label: string | null; low: number | null; high: number | null };

/**
 * Per-payment points: `history.quarterly` (every regular dividend of the last 10 years, then the
 * events to come; specials are not in it), else the ladder's declared and forecast rows.
 */
export const paymentPoints = (history: DivHistory | null, ladder: DivLadderRow[] | null): PaymentPoint[] => {
    if (history?.quarterly?.length)
        return history.quarterly
            .filter((q): q is DivPayment & { ex_date: string; amount: number } => !!q.ex_date && isNum(q.amount))
            .map((q) => ({ ex_date: q.ex_date, amount: q.amount, forecast: q.forecast, special: false, label: q.period_label, low: q.amount_p10 ?? null, high: q.amount_p90 ?? null }));
    const out: PaymentPoint[] = [];
    for (const r of ladder ?? []) {
        const amount = r.past ? (r.declared_amount ?? r.amount_p50) : r.amount_p50;
        const forecast = !r.past && r.status !== "declared";
        if (r.ex_date && isNum(amount)) out.push({ ex_date: r.ex_date, amount, forecast, special: r.kind === "special", label: r.period_label, low: forecast ? r.amount_p10 : null, high: forecast ? r.amount_p90 : null });
    }
    return out.sort((a, b) => a.ex_date.localeCompare(b.ex_date));
};

export const hasHistory = (p: DivStockResponse): boolean => !p.locked && !!p.history && (p.history.annual?.length ?? 0) > 0;

const yearMs = (year: number) => Date.UTC(year, 6, 1);
const isoMs = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

/** The chart's spans: it opens on the last 15 years and scrolls (or a button jumps) back to the first payment. */
const HISTORY_RANGES: RangeButton[] = [
    { label: "10Y", days: 3653 },
    { label: "15Y", days: 5479 },
    { label: "20Y", days: 7305 },
    { label: "All", days: 0 },
];
const HISTORY_OPEN_RANGE = "15Y";
/** Range buttons only once the record is longer than the shortest of them. */
const HISTORY_RANGE_MIN_YEARS = 11;

const KIND_LABEL: Record<string, string> = { regular: "Regular", special: "Special", return_of_capital: "Return of capital", liquidation: "Liquidation" };

const moneyCell = (value: number | null, row: DivHistoryGridRow): ReactNode => <div className={divStyles.cellNum}>{row.locked && value === null ? <Blurred>0.00</Blurred> : (fmtMoney(value, row.currency) ?? "-")}</div>;

/** The history list's own cells: amounts per share in the row's currency, kinds in words. */
const HISTORY_CELLS: Record<string, (row: DivHistoryGridRow) => ReactNode> = {
    amount: (row) => moneyCell(row.amount, row),
    amountDeclared: (row) => moneyCell(row.amountDeclared, row),
    kind: (row) => <div className={divStyles.cell}>{row.kind ? (KIND_LABEL[row.kind] ?? row.kind) : "-"}</div>,
};

/**
 * The history list: every payment of the listing, newest first, in the standard DataGrid
 * (`stock/<tradingitem_id>/history/list`, TED #2503). It fills the box and scrolls itself; no CSV.
 */
const HistoryList: FC<{ payload: DivStockResponse }> = ({ payload }) => {
    const demo = useDemoMode();
    const id = payload.tradingitem_id;
    const fetcher = useMemo(() => (demo ? demoHistoryGridFetcher(payload) : divStockHistoryGridFetcher(id)), [demo, id, payload]);
    return (
        <div className={styles.historyList}>
            <div className={styles.historyGrid}>
                <MemoizedDataGrid key={`${demo ? "demo" : "live"}:${id}`} fetcher={fetcher} rowsAmount={DIV_ONE_PAGE_ROWS} tableId="dividends-stock-history" cellRenderers={HISTORY_CELLS} customRowHeight={32} noRowsMessage="No dividends to list." />
            </div>
        </div>
    );
};

/** One point of the history chart: paid, or a forecast with its range when it has one. */
export type FanPoint = { time: number; amount: number; forecast: boolean; low: number | null; high: number | null };

/**
 * The history chart's series: paid columns, and for the forecast a fan (TED #2458): the range of each
 * forecast year or payment as a translucent bar from its low to its high end (widening with the
 * horizon), and the central amount as a dashed line from the last paid point. Forecast points
 * without a range (older payloads) stay lighter columns. `others` are the dates of the box's other
 * series (specials): they join the range's timeline too, so their columns keep drawing.
 */
export const fanSeries = (points: FanPoint[], accent: string, others: [number, number][] = []): TimeSeries[] => {
    const paid = points.filter((p) => !p.forecast);
    const fc = points.filter((p) => p.forecast);
    const banded = fc.filter((p) => isNum(p.low) && isNum(p.high));
    const out: TimeSeries[] = [{ id: "paid", title: "Paid", type: "column", data: paid.map((p) => [p.time, p.amount] as [number, number]), color: accent, align: "exact", precision: 2, format: "price" }];
    const plain = fc.filter((p) => !(isNum(p.low) && isNum(p.high)));
    if (plain.length) out.push({ id: "forecast", title: "Forecast", type: "column", data: plain.map((p) => [p.time, p.amount] as [number, number]), color: alpha(accent, 0.38), align: "exact", precision: 2, format: "price" });
    if (banded.length) {
        // A candlestick series sets the chart's timeline, so every paid point (and every other series'
        // date) is in it too, as an invisible "down" bar (downColor transparent); the ranges are the
        // "up" bars (low to high). One bar per date, in time order; a range wins its date.
        const hidden = (time: number, amount: number) => ({ time, open: amount * 1.000001, high: amount * 1.000001, low: amount, close: amount, volume: 0 });
        const byTime = new Map<number, ReturnType<typeof hidden>>();
        for (const [time, amount] of others) byTime.set(time, hidden(time, amount));
        for (const p of paid) byTime.set(p.time, hidden(p.time, p.amount));
        for (const p of banded) byTime.set(p.time, { time: p.time, open: p.low as number, high: p.high as number, low: p.low as number, close: p.high as number, volume: 0 });
        const bars = Array.from(byTime.values()).sort((a, b) => a.time - b.time);
        out.push({ id: "range", title: "Range", type: "candlestick", data: bars, upColor: alpha(accent, 0.22), downColor: "rgba(0,0,0,0)", align: "exact", precision: 2, format: "price", tooltip: false, legend: false, zIndex: 0 });
        const last = paid[paid.length - 1];
        const central = [...(last && last.time < banded[0].time ? [last] : []), ...banded].map((p) => [p.time, p.amount] as [number, number]);
        out.push({ id: "central", title: "Forecast", type: "line", data: central, color: accent, lineStyle: "dashed", lineWidth: 2, align: "exact", precision: 2, format: "price", zIndex: 2 });
    }
    return out;
};

/**
 * Dividends per share by year (or per payment) back to the listing's first, the forecast years as a
 * fan that widens with the horizon, and the growth figures; or, as a list, every payment with its dates.
 */
export const HistoryBox: FC<{ payload: DivStockResponse }> = ({ payload }) => {
    const h = payload.history;
    const accent = useAtomValue(ortexColorState);
    const neutral = useAtomValue(neutralState);
    const [view, setView] = useState<"annual" | "payment">("annual");
    const [mode, setMode] = useState<"chart" | "list">("chart");
    const payments = useMemo(() => paymentPoints(h, payload.ladder), [h, payload.ladder]);
    const points = useMemo<FanPoint[]>(() => {
        if (!h) return [];
        if (view === "annual")
            return h.annual.filter((p) => isNum(p.amount)).map((p) => ({ time: yearMs(p.year), amount: p.amount as number, forecast: p.forecast, low: p.forecast ? (p.amount_p10 ?? null) : null, high: p.forecast ? (p.amount_p90 ?? null) : null }));
        return payments.filter((p) => !p.special).map((p) => ({ time: isoMs(p.ex_date), amount: p.amount, forecast: p.forecast, low: p.low, high: p.high }));
    }, [h, view, payments]);
    const series = useMemo<TimeSeries[]>(() => {
        const special = view === "payment" ? payments.filter((p) => p.special && !p.forecast).map((p) => [isoMs(p.ex_date), p.amount] as [number, number]) : [];
        const out = fanSeries(points, accent, special);
        if (special.length) out.push({ id: "special", title: "Special", type: "column", data: special, color: neutral, align: "exact", precision: 2, format: "price" });
        return out;
    }, [points, view, payments, accent, neutral]);
    const ranges = useMemo(() => new Map(points.filter((p) => p.forecast && isNum(p.low) && isNum(p.high)).map((p) => [p.time, p])), [points]);
    if (!h || payload.locked || !h.annual?.length) return null;
    const canPay = payments.length > 1;
    const years = h.annual[h.annual.length - 1].year - h.annual[0].year + 1;
    const ranged = years >= HISTORY_RANGE_MIN_YEARS;
    const canList = !!payload.tradingitem_id;
    const list = mode === "list" && canList;
    const hasFan = ranges.size > 0;
    const lastYear = h.annual.reduce((m, p) => Math.max(m, p.year), 0);
    const share = h.annual.find((p) => p.year === lastYear)?.paying_share;
    const yearsAhead = payload.as_of ? lastYear - Number(payload.as_of.slice(0, 4)) : 0;
    const tooltipTitle = (t: number) => (view === "annual" ? `${new Date(t).getUTCFullYear()}` : (fmtDate(new Date(t).toISOString().slice(0, 10), true) ?? ""));
    const tooltipExtra = (t: number) => {
        const p = ranges.get(t);
        return p && isNum(p.low) && isNum(p.high) ? [{ label: "Range", value: `${fmtMoney(p.low, payload.currency)}–${fmtMoney(p.high, payload.currency)}`, color: alpha(accent, 0.5) }] : [];
    };
    return (
        <Box
            title="Dividend history"
            maximizable
            help={
                <>
                    Regular dividends per share by year (or per payment) since the first one, adjusted for splits and rights issues so the years compare on today&apos;s share count. Forecast years show the central forecast as a dashed line and its
                    range as a shaded band that widens with each year ahead: tested on past years, 8 in 10 outcomes fell inside it for companies that kept paying (lighter bars where there is no range). Specials are not in the bars. The chart opens on
                    the 15 years to the last forecast year: drag it or scroll on it to go back, or pick a span (All reaches the first payment). <b>List</b> shows every payment, newest first, with its ex, pay, record and announcement dates, the amount
                    on today&apos;s shares beside the amount as declared, its frequency and its kind.
                </>
            }
            note={
                list ? (
                    <>per share, newest first</>
                ) : (
                    <>
                        per share, split-adjusted · <span className={styles.swatch} style={{ background: hasFan ? alpha(accent, 0.22) : alpha(accent, 0.38) }} />
                        {hasFan ? "forecast range" : "forecast"}
                    </>
                )
            }
            action={
                <span className={styles.toggles}>
                    {canPay && !list && (
                        <span className={styles.toggle} role="group" aria-label="History view">
                            <button type="button" aria-pressed={view === "annual"} onClick={() => setView("annual")}>
                                Annual
                            </button>
                            <button type="button" aria-pressed={view === "payment"} onClick={() => setView("payment")}>
                                {payload.yield?.payments_per_year === 4 || payload.next?.frequency === "quarterly" ? "Quarterly" : "Per payment"}
                            </button>
                        </span>
                    )}
                    {canList && (
                        <span className={styles.toggle} role="group" aria-label="Chart or list">
                            <button type="button" aria-pressed={!list} onClick={() => setMode("chart")}>
                                Chart
                            </button>
                            <button type="button" aria-pressed={list} onClick={() => setMode("list")}>
                                List
                            </button>
                        </span>
                    )}
                </span>
            }
        >
            {list ? (
                <HistoryList payload={payload} />
            ) : (
                <>
                    <div className={styles.chart}>
                        <TimeSeriesChart
                            // A new view opens on the same span again.
                            key={view}
                            series={series}
                            legend={false}
                            rangeButtons={ranged ? HISTORY_RANGES : false}
                            initialRange={ranged ? HISTORY_OPEN_RANGE : undefined}
                            exportMenu={false}
                            exportName={`${payload.ticker}_dividend_history`}
                            resolution={view === "annual" ? "12M" : "1D"}
                            maxBarSpacing={480}
                            crosshair
                            tooltipTitle={tooltipTitle}
                            tooltipExtra={tooltipExtra}
                            height="100%"
                            style={{ position: "absolute", inset: 0 }}
                        />
                    </div>
                    {hasFan && (
                        <div className={styles.foot}>
                            Shaded range: 8 in 10 past outcomes fell inside
                            {isNum(share) && yearsAhead > 0 ? ` · ${fmtPct(share * 100, 0)} of comparable payers still paying in ${lastYear}` : ""}
                        </div>
                    )}
                    <div className={styles.figs}>
                        <Fig label="Growth 1y" value={fmtPctSigned(h.growth_1y_pct)} />
                        <Fig label="Growth 5y" value={fmtPctSigned(h.growth_5y_cagr_pct) ? `${fmtPctSigned(h.growth_5y_cagr_pct)} a year` : null} />
                        <Fig label="Raises in a row" value={isNum(h.raises_in_a_row) ? String(h.raises_in_a_row) : null} />
                        <Fig label="Cuts since 2012" value={isNum(h.cuts_since_2012) ? String(h.cuts_since_2012) : null} note={isNum(h.specials) ? `specials: ${h.specials === 0 ? "none" : h.specials}` : null} />
                    </div>
                </>
            )}
        </Box>
    );
};

// ─── Small boxes ─────────────────────────────────────────────────────────────────

const meterClass = (pct: number) => (pct > 100 ? `${styles.meter} ${styles.meterBad}` : pct > 75 ? `${styles.meter} ${styles.meterWarn}` : styles.meter);

const MeterRow: FC<{ label: string; pct: number | null }> = ({ label, pct }) => (
    <div className={styles.meterRow}>
        <span>{label}</span>
        <b>{isNum(pct) ? fmtPct(pct, 0) : "n/a"}</b>
        <div className={isNum(pct) ? meterClass(pct) : `${styles.meter} ${styles.meterWarn}`} role="img" aria-label={`${label}: ${isNum(pct) ? fmtPct(pct, 0) : "not available"}`}>
            <i style={{ width: `${isNum(pct) ? Math.max(2, Math.min(100, pct)) : 100}%`, opacity: isNum(pct) ? 1 : 0.35 }} />
        </div>
    </div>
);

export const hasCoverage = (c: DivCoverage | null): boolean => !!c && [c.payout_earnings_fwd_pct, c.payout_fcf_pct, c.payout_fcf_after_buybacks_pct, c.net_cash_per_share, c.cover_multiple].some(isNum);

/** Coverage: the dividend against earnings, free cash flow and free cash flow after buybacks. */
export const CoverageBox: FC<{ coverage: DivCoverage | null; forecasts?: boolean }> = ({ coverage: c, forecasts = true }) => {
    if (!c || !hasCoverage(c)) return null;
    return (
        <Box title="Coverage" help="How much of earnings and of free cash flow the dividend uses. Above 75% the bar turns amber; above 100% the dividend takes more than the company earns or generates.">
            {forecasts && <MeterRow label="Dividend / earnings (fwd)" pct={c.payout_earnings_fwd_pct} />}
            <MeterRow label="Dividend / free cash flow" pct={c.payout_fcf_pct} />
            <MeterRow label="Dividend / (FCF − buybacks)" pct={c.payout_fcf_after_buybacks_pct} />
            {isNum(c.net_cash_per_share) && (
                <div className={styles.meterRow}>
                    <span>{c.net_cash_per_share >= 0 ? "Net cash per share" : "Net debt per share"}</span>
                    <b>{fmtAmount(Math.abs(c.net_cash_per_share))}</b>
                </div>
            )}
            {isNum(c.cover_multiple) && <div className={styles.foot}>{c.cover_multiple >= 1 ? `The dividend is ${c.cover_multiple.toFixed(1)}× covered by earnings.` : `Earnings cover ${c.cover_multiple.toFixed(1)}× of the dividend.`}</div>}
        </Box>
    );
};

const POLICY_TYPE: Record<string, string> = { progressive: "Progressive", stable: "Stable", payout_ratio: "Payout ratio", variable: "Variable", none: "None stated" };

export const hasPolicy = (p: DivPolicy | null): boolean => !!p && !!(p.quote || p.type || isNum(p.last_change_pct) || isNum(p.guidance_amount) || p.guidance_text);

/** The company's own words on its dividend, with the last change. */
export const PolicyBox: FC<{ policy: DivPolicy | null }> = ({ policy: p }) => {
    if (!p || !hasPolicy(p)) return null;
    return (
        <Box
            title="Stated policy"
            help="The dividend policy the record shows (progressive after five years of raises, stable after three flat years), the last change, and any dividend guidance the company has given."
            action={
                p.source_url ? (
                    <a className={styles.titleLink} href={p.source_url} target="_blank" rel="noopener noreferrer">
                        Source
                    </a>
                ) : null
            }
        >
            {p.quote && (
                <div className={styles.commentary}>
                    <p>
                        &ldquo;{p.quote}&rdquo;{p.source_label ? ` (${p.source_label})` : ""}
                    </p>
                </div>
            )}
            <dl className={styles.kv}>
                {p.type && (
                    <>
                        <dt>Policy type</dt>
                        <dd>{POLICY_TYPE[p.type] ?? p.type}</dd>
                    </>
                )}
                {isNum(p.last_change_pct) && (
                    <>
                        <dt>Last change</dt>
                        <dd>
                            {fmtPctSigned(p.last_change_pct)}
                            {fmtMonth(p.last_change_date) ? ` · ${fmtMonth(p.last_change_date)}` : ""}
                        </dd>
                    </>
                )}
                <dt>Guidance DPS</dt>
                <dd>{fmtAmount(p.guidance_amount) ?? "none"}</dd>
            </dl>
            {p.guidance_text && <div className={styles.foot}>{p.guidance_text}</div>}
        </Box>
    );
};

export const hasBorrow = (b: DivBorrow | null): boolean => !!b && ((b.series?.length ?? 0) > 1 || isNum(b.short_interest_pct_ff));

/**
 * ORTEX short interest around the last record date, as a share of free float, with the record date
 * marked. The record-date spike reads securities lending: borrowing to capture a dividend is what
 * ORTEX's dividend adjuster takes out of short interest.
 */
export const BorrowBox: FC<{ borrow: DivBorrow | null; href: string | null }> = ({ borrow: b, href }) => {
    if (!b || !hasBorrow(b)) return null;
    const pts = (b.series ?? []).filter(([t, v]) => isNum(t) && isNum(v));
    const W = 220;
    const H = 70;
    let path: string | null = null;
    let markX: number | null = null;
    if (pts.length > 1) {
        const t0 = pts[0][0];
        const t1 = pts[pts.length - 1][0];
        const vs = pts.map((p) => p[1]);
        // From zero, so a flat short-interest line reads flat and a real move stands out.
        const hi = Math.max(...vs, 0);
        const top = hi > 0 ? hi * 1.5 : 1;
        const x = (t: number) => ((t - t0) / Math.max(1, t1 - t0)) * (W - 8) + 4;
        const y = (v: number) => H - 4 - (Math.max(0, v) / top) * (H - 14);
        path = pts.map(([t, v], i) => `${i ? "L" : "M"}${x(t).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
        if (b.last_record_date) {
            const rt = Date.parse(`${b.last_record_date}T00:00:00Z`);
            if (rt >= t0 && rt <= t1) markX = x(rt);
        }
    }
    const spike = b.record_spike === null || b.record_spike === undefined ? null : b.record_spike ? "Yes" : "None";
    return (
        <Box
            title="Short interest around record date"
            help={
                <>
                    ORTEX short interest as a share of free float, from a month before the last record date to ten days after it. <b>Record-date borrowing spike</b> says whether shares were borrowed to capture the dividend (securities lending around
                    the record date), which ORTEX takes out of short interest.
                </>
            }
            action={<TitleLink href={href}>Short interest</TitleLink>}
        >
            {path && (
                <div className={styles.sparkWrap}>
                    <svg
                        className={styles.spark}
                        viewBox={`0 0 ${W} ${H}`}
                        preserveAspectRatio="none"
                        role="img"
                        aria-label={`Short interest around the last record date${b.last_record_date ? `, last record date ${fmtDate(b.last_record_date, true)} marked` : ""}`}
                    >
                        {markX !== null && <line className={styles.sparkMark} x1={markX} x2={markX} y1={4} y2={H - 2} />}
                        <path className={styles.sparkLine} d={path} />
                    </svg>
                    {markX !== null && (
                        <span className={styles.sparkLabel} style={markX > W * 0.6 ? { right: `${((W - markX) / W) * 100 + 2}%` } : { left: `${(markX / W) * 100 + 2}%` }}>
                            record {fmtDate(b.last_record_date)}
                        </span>
                    )}
                </div>
            )}
            <dl className={styles.kv}>
                {isNum(b.short_interest_pct_ff) && (
                    <>
                        <dt>Short interest</dt>
                        <dd>{fmtPct(b.short_interest_pct_ff)} of free float</dd>
                    </>
                )}
                {isNum(b.utilisation_pct) && (
                    <>
                        <dt>Utilisation</dt>
                        <dd>{fmtPct(b.utilisation_pct)}</dd>
                    </>
                )}
                {isNum(b.ctb_pct) && (
                    <>
                        <dt>Cost to borrow</dt>
                        <dd>{fmtPct(b.ctb_pct)}</dd>
                    </>
                )}
                {spike && (
                    <>
                        <dt>Record-date borrowing spike</dt>
                        <dd className={b.record_spike ? styles.down : undefined}>{spike}</dd>
                    </>
                )}
            </dl>
        </Box>
    );
};

export const hasOptionsImplied = (o: DivOptionsRow[] | null): boolean => !!o && o.some((r) => isNum(r.implied));

/** Cumulative dividends to each expiry that the options prices imply, beside the forecast. */
export const OptionsBox: FC<{ options: DivOptionsRow[] | null; href: string | null }> = ({ options, href }) => {
    if (!options || !hasOptionsImplied(options)) return null;
    return (
        <Box title="What options imply" help="The dividends option prices imply to each expiry (put-call parity on near-the-money strikes), beside the ORTEX forecast for the same period." action={<TitleLink href={href}>Options</TitleLink>}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Expiry</th>
                        <th className={styles.num}>Implied</th>
                        <th className={styles.num}>Forecast</th>
                    </tr>
                </thead>
                <tbody>
                    {options.slice(0, 6).map((r) => (
                        <tr key={r.expiry}>
                            <td>{fmtDateShort(r.expiry)}</td>
                            <td className={styles.num}>{fmtAmount(r.implied)}</td>
                            <td className={styles.num}>{fmtAmount(r.forecast)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className={styles.foot}>Cumulative dividends to expiry, from put-call parity on near-the-money strikes.</div>
        </Box>
    );
};

export const hasExBehavior = (e: DivExBehavior | null): boolean => !!e && [e.drop_ratio, e.runup_10d_pct, e.short_volume_after_pct, e.capture_return_pct].some(isNum);

/** How the price and short selling behave around the ex-date. */
export const ExBehaviorBox: FC<{ ex: DivExBehavior | null }> = ({ ex: e }) => {
    if (!e || !hasExBehavior(e)) return null;
    return (
        <Box
            title="Ex-date behavior"
            help="How the price behaved around this stock's past ex-dates: the drop on the ex-date as a multiple of the dividend, the run-up in the ten days before, and the return from buying the day before and selling on the ex-date."
        >
            <div className={styles.figs}>
                <Fig label="Price drop on ex-date" value={isNum(e.drop_ratio) ? `${e.drop_ratio.toFixed(1)}×` : null} note={isNum(e.ex_dates) ? `of the dividend, median of ${e.ex_dates} ex-dates` : "of the dividend, 3-year median"} />
                <Fig label="Run-up 10d before" value={fmtPctSigned(e.runup_10d_pct)} />
                <Fig label="Short volume after ex" value={fmtPctSigned(e.short_volume_after_pct, 0)} note="next 3 sessions" />
                <Fig label="Capture return" value={fmtPctSigned(e.capture_return_pct, 2)} note="buy T−1, sell T, net of drop" />
            </div>
        </Box>
    );
};

const LIST_STATUS: Record<string, string> = { member: "Member", candidate: "Candidate", at_risk: "At risk", removed: "Removed", "not yet": "Not yet" };

/** Dividend lists and indices the stock is in, or close to. */
export const ListsBox: FC<{ lists: DivListMembership[] | null; href: string | null }> = ({ lists, href }) => {
    if (!lists || !lists.length) return null;
    return (
        <Box title="Lists and index effects" help="Dividend lists and indices the stock is in, or close to joining or leaving." action={<TitleLink href={href}>Index</TitleLink>}>
            <ul className={styles.list}>
                {lists.map((l) => (
                    <li key={l.name}>
                        <span className={styles.listName}>{l.name}</span>
                        <span className={styles.listValue}>{[LIST_STATUS[l.status] ?? l.status, l.note].filter(Boolean).join(" · ")}</span>
                    </li>
                ))}
            </ul>
        </Box>
    );
};

/** Sector peers' next declarations, each linking to its own Dividend tab. */
export const PeersBox: FC<{ peers: DivPeer[] | null; linkFor: (p: DivPeer) => string }> = ({ peers, linkFor }) => {
    if (!peers || !peers.length) return null;
    return (
        <Box title="Peers" note="next declarations" help="Sector peers: the next dividend per share (in each peer's own currency) and its expected declaration date. Click one for its Dividend tab.">
            <ul className={styles.list}>
                {peers.slice(0, 6).map((p) => (
                    <li key={`${p.exchange_symbol}:${p.ticker}`}>
                        <Link href={linkFor(p)} className={styles.peerLink}>
                            <IrLogo row={{ isin: null, ticker: p.ticker, exchange: p.exchange_symbol, locked: false }} size={18} />
                            <span className={styles.listName}>
                                {p.company_name} <span className={styles.band}>{p.ticker}</span>
                            </span>
                        </Link>
                        <span className={styles.listValue}>
                            {[fmtAmount(p.next_amount), fmtDate(p.next_declaration_date)].filter(Boolean).join(" · ")} <OutcomePill outcome={p.outcome} />
                        </span>
                    </li>
                ))}
            </ul>
        </Box>
    );
};
